import React, { useEffect, useMemo, useState } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { checkIsTimeSameOrAfter } from '../../../Events/Services/eventInterval.service';
import useTranslation from '../../../../Hooks/useTranslation';
import { useInputValidation } from '../../../Base/Hooks/useInputValidation';
import Text from '../../../../Components/Base/Text/Text';
import FormControl from '../../../../Components/Form/FormControl';
import { useAppContext } from '../../../../Services/AppContext';
import DateField from '../../../../Components/Form/components/DateField';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import SelectField from '../../../../Components/Form/components/SelectField';
import RadioListField from '../../../../Components/Form/components/RadioListField';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import CalendarTimezoneWarning from '../../../../Components/Calendar/CalendarTimezoneWarning/CalendarTimezoneWarning';
import TimePicker from '../../../../Components/Base/TimePicker/TimePicker';
import TimeZoneInfo from '../../../../Components/TimeZoneInfo/TimeZoneInfo';
import { getTimezonesDifference } from '../../../Calendar/Utils/getTimezonesDifference';
import FormattedPriceInputField from '../../../Base/Components/Inputs/FormattedPriceInput/FormattedPriceInputField';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import { Moment } from 'moment';
import { ServiceScheduleType } from '../../../../graphql/graphql';
import NumberField from '../../../../Components/Form/components/NumberField';
import { useServiceScheduleContext } from '../../../WorkplaceMember/Services/ServiceScheduleContext';
import useWorkingHours from '../../Hooks/useWorkingHours';
import { RepeatInterval } from '../../Enums/RepeatInterval.enum';
import { Theme } from '../../../../Enums/Theme.enum';
import LinkButton from '../../../../Components/Button/LinkButton';
import ScheduleRepeatFields from '../ScheduleRepeatFields';

interface Props {
	onSubmit: (values: ServiceScheduleShiftFormValues) => void;
	submitButtonText?: string;
	defaultValues?: Record<string, any>;
	onDelete?: () => void;
	isEdit?: boolean;
}

export interface ServiceScheduleShiftFormValues {
	date: Moment;
	extraPayment: boolean;
	individualTime: boolean;
	guaranteedTime?: number;
	price?: string;
	repeatInterval: SelectValue;
	repeatTo?: Moment;
	timeFrom?: Moment;
	timeTo?: Moment;
	workingTime?: ServiceScheduleType;
	workplaceMember: SelectValue;
	specificDaysInWeek?: {
		mon: boolean;
		tue: boolean;
		wed: boolean;
		thu: boolean;
		fri: boolean;
		sat: boolean;
		sun: boolean;
	};
	limitMaxActiveCases?: boolean;
	maxActiveCases?: number;
}

const ServiceScheduleShiftForm: React.FC<Props> = ({
	onSubmit,
	defaultValues,
	submitButtonText,
	onDelete,
	isEdit,
}) => {
	const { t } = useTranslation();
	const { getPriceInputValidation } = useInputValidation();
	const { isLatam, defaultTimeZone } = useAppContext();
	const loggedUserTimeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
	const [isLoading, setIsLoading] = useState(false);
	const [hasExtraPayment, setHasExtraPayment] = useState(false);
	const [hasTimes, setHasTimes] = useState(false);

	const { workplaceMembersSelectValues } = useServiceScheduleContext();

	const { diffInMinutes, formattedDiff } = useMemo(
		() =>
			getTimezonesDifference({
				timezone1: loggedUserTimeZone,
				timezone2: defaultTimeZone,
			}),
		[loggedUserTimeZone, defaultTimeZone],
	);

	const validationSchema = yup.object({
		date: yup
			.date()
			.required(t('validation.calendarWorkplaceScheduleEvent.date')),
		workplaceMember: yup
			.mixed()
			.required(
				t('validation.calendarWorkplaceScheduleEvent.workplaceMembers'),
			),
		repeatInterval: yup.object().shape({
			value: yup
				.string()
				.required(
					t('validation.calendarWorkplaceScheduleEvent.repeatInterval'),
				),
		}),
		workingTime: yup
			.mixed()
			.required(t('validation.calendarWorkplaceScheduleEvent.workingTime')),
		individualTime: yup.boolean().notRequired(),
		timeFrom: yup
			.date()
			.required(t('validation.calendarWorkplaceScheduleEvent.timeFrom')),
		timeTo: yup
			.date()
			.test(
				'is-greater',
				t('validation.calendarWorkplaceScheduleEvent.timeDiff') ?? ' ',
				function () {
					const { date, timeFrom, timeTo } = this.parent;
					return checkIsTimeSameOrAfter(date, timeFrom, timeTo);
				},
			)
			.required(t('validation.calendarWorkplaceScheduleEvent.timeTo')),
		guaranteedTime: !hasExtraPayment
			? yup.mixed().notRequired()
			: yup
					.number()
					.typeError(t('validation.form.mustFillNumber'))
					.required(t('validation.calendarWorkplaceScheduleEvent.workingTime')),
		price: !hasExtraPayment
			? yup.mixed().notRequired()
			: getPriceInputValidation(
					'validation.calendarWorkplaceScheduleEvent.price',
					true,
			  ),
		repeatTo: yup.mixed().when('repeatInterval', {
			is: ({ value }: { value: string }) => value !== RepeatInterval.NEVER,
			then: yup
				.date()
				.required(t('validation.calendarWorkplaceScheduleEvent.repeatTo'))
				.typeError(t('validation.calendarWorkplaceScheduleEvent.repeatTo'))
				.test(
					'is-after-date',
					t('validation.calendarWorkplaceScheduleEvent.repeatToAfterDate'),
					function (repeatTo) {
						const { date } = this.parent;
						if (!repeatTo || !date) return true;
						return repeatTo > date;
					},
				)
				.default(undefined),
		}),
		specificDaysInWeek: yup
			.mixed()
			.when('repeatInterval', {
				is: ({ value }: { value: string }) =>
					value === RepeatInterval.SPECIFIC_DAYS_IN_WEEK,
				then: yup
					.object({
						mon: yup.boolean().required(),
						tue: yup.boolean().required(),
						wed: yup.boolean().required(),
						thu: yup.boolean().required(),
						fri: yup.boolean().required(),
						sat: yup.boolean().required(),
						sun: yup.boolean().required(),
					})
					.test(
						'at-least-one-true',
						t('validation.calendarWorkplaceScheduleEvent.specificDaysInWeek'),
						days => Object.values(days).some(Boolean),
					),
			})
			.default(undefined),
		limitMaxActiveCases: yup.boolean().required(),
		maxActiveCases: yup
			.mixed()
			.nullable()
			.when('limitMaxActiveCases', {
				is: true,
				then: yup
					.number()
					.typeError(
						'validation.serviceScheduleShiftForm.limitMaxActiveCases.typeError',
					)
					.required(
						'validation.serviceScheduleShiftForm.limitMaxActiveCases.required',
					)
					.integer(
						'validation.serviceScheduleShiftForm.limitMaxActiveCases.typeError',
					)
					.min(
						1,
						'validation.serviceScheduleShiftForm.limitMaxActiveCases.min',
					),
				otherwise: schema => schema.notRequired(),
			}),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	const hasIndividualTime = formMethods.watch('individualTime');
	const extraPaymentValue = formMethods.watch('extraPayment');
	const limitMaxActiveCasesValue = formMethods.watch('limitMaxActiveCases');
	const date = formMethods.watch('date');
	const workingTime = formMethods.watch('workingTime');

	const { workingHoursOptions, getTimesByShiftType } = useWorkingHours(date);

	useEffect(() => {
		setHasExtraPayment(extraPaymentValue);
	}, [extraPaymentValue]);

	useEffect(() => {
		const times = getTimesByShiftType(workingTime);
		if (times) {
			if (isEdit) {
				formMethods.setValue('individualTime', true);
			} else {
				//IF IS NOT EDIT
				formMethods.setValue('timeFrom', times.timeFrom);
				formMethods.setValue('timeTo', times.timeTo);
				setHasTimes(true);
			}
		} else {
			formMethods.setValue('individualTime', true);
		}
	}, [workingTime]);

	const handleSubmit = (values: any) => {
		setIsLoading(true);
		onSubmit(values as ServiceScheduleShiftFormValues);
		setIsLoading(false);
	};

	return (
		<FormWrapper formMethods={formMethods}>
			{!isLatam && (
				<div className='mb-3'>
					<Text
						tx='component.calendarWorkplaceScheduleEvent.setupToWhoCanPatientsMakeOrders'
						style={{ fontWeight: 600 }}
					/>
				</div>
			)}
			<InputWrapper>
				<DateField name='date' label='base.shared.date' isRequired />
			</InputWrapper>
			<InputWrapper>
				<SelectField
					label='base.selectWorker'
					name='workplaceMember'
					options={workplaceMembersSelectValues}
					placeholder={t('base.shared.pick')}
					isRequired
					isSearchable
				/>
			</InputWrapper>
			{!defaultValues?.repeatable && (
				<ScheduleRepeatFields formMethods={formMethods} />
			)}
			{!isEdit && (
				<RadioListField
					label='base.workingTime'
					name='workingTime'
					data={workingHoursOptions}
					inLine
					isRequired
					isWrapped
				/>
			)}
			{!isEdit && hasTimes && (
				<CheckboxField
					name='individualTime'
					tx='base.pickIndividualTime'
					firstUpper
					isWrapped
				/>
			)}
			{hasIndividualTime && (
				<>
					{diffInMinutes > 0 && (
						<CalendarTimezoneWarning
							timezone={defaultTimeZone}
							timeDiff={formattedDiff}
							textKeys={{
								part1: 'base.workplaceTimeIsInTimeZone.part1',
								part2: 'base.workplaceTimeIsInTimeZone.part2',
								hourMiddle: 'base.shared.hourMiddle',
							}}
						/>
					)}
					<div className={'row'}>
						<div className={'col-6'}>
							<InputWrapper>
								<FormControl
									label='base.shared.from'
									name='timeFrom'
									isRequired>
									<TimePicker />
								</FormControl>
								<TimeZoneInfo
									loggedUserTimeZone={loggedUserTimeZone}
									comparedTimeZone={defaultTimeZone}
								/>
							</InputWrapper>
						</div>
						<div className={'col-6'}>
							<InputWrapper>
								<FormControl label='base.shared.to' name='timeTo' isRequired>
									<TimePicker />
								</FormControl>
								<TimeZoneInfo
									loggedUserTimeZone={loggedUserTimeZone}
									comparedTimeZone={defaultTimeZone}
								/>
							</InputWrapper>
						</div>
					</div>
				</>
			)}
			<CheckboxField
				name='extraPayment'
				tx='component.caseType.atThisTimePatientHasToPayFee'
				isWrapped
			/>
			{hasExtraPayment && (
				<>
					<InputWrapper>
						<NumberField
							name='guaranteedTime'
							label='component.caseType.guaranteedToConnectIn'
							suffix='base.minutes2'
							isRequired
						/>
					</InputWrapper>
					<FormattedPriceInputField
						name='price'
						label='component.caseType.feeAmount'
						isRequired
					/>
				</>
			)}
			<CheckboxField
				name='limitMaxActiveCases'
				tx='component.serviceScheduleShiftForm.limitMaxActiveCases'
				isWrapped
			/>
			{limitMaxActiveCasesValue && (
				<NumberField
					name='maxActiveCases'
					label='component.serviceScheduleShiftForm.maxActiveCases'
					minValue={1}
					integerOnly
					isRequired
				/>
			)}
			<div
				className={'d-flex flex-row align-items-center mt-4'}
				style={{ gap: '10px' }}>
				<SubmitButton
					title={submitButtonText}
					onSubmit={handleSubmit}
					disabled={isLoading}
				/>
				{onDelete && (
					<LinkButton
						theme={Theme.RED}
						title='base.deleteSchedule'
						onClick={onDelete}
						firstUpper
					/>
				)}
			</div>
		</FormWrapper>
	);
};

export default ServiceScheduleShiftForm;
